import React from 'react';
import Icon from '../AppIcon';

// Soft gradient backgrounds cycling through warm tones
const ACCENT_COLORS = [
  { bg: 'from-amber-500/20 to-orange-500/10', border: 'border-amber-400/30', icon: 'text-amber-400', btn: 'bg-amber-500 hover:bg-amber-600' },
  { bg: 'from-sky-500/20 to-blue-500/10', border: 'border-sky-400/30', icon: 'text-sky-400', btn: 'bg-sky-500 hover:bg-sky-600' },
  { bg: 'from-emerald-500/20 to-teal-500/10', border: 'border-emerald-400/30', icon: 'text-emerald-400', btn: 'bg-emerald-500 hover:bg-emerald-600' },
  { bg: 'from-violet-500/20 to-purple-500/10', border: 'border-violet-400/30', icon: 'text-violet-400', btn: 'bg-violet-500 hover:bg-violet-600' },
];

const getAccent = (messageId) => ACCENT_COLORS?.[(messageId - 1) % ACCENT_COLORS?.length];

// ─── Popup ────────────────────────────────────────────────────────────────────

const MotivationalPopup = ({ message, userName, onDismiss }) => {
  if (!message) return null;
  const accent = getAccent(message?.id);
  const firstName = userName?.split(' ')?.[0] || userName || '';

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className={`relative w-full max-w-md bg-card border ${accent?.border} rounded-2xl shadow-2xl overflow-hidden`}>
        {/* Gradient top strip */}
        <div className={`h-1.5 w-full bg-gradient-to-r ${accent?.bg}`} />

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-2">
          <div className="flex items-center gap-2.5">
            <div className={`p-2 rounded-xl bg-gradient-to-br ${accent?.bg}`}>
              <Icon name="Sparkles" size={18} className={accent?.icon} />
            </div>
            <div>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Daily Motivation
              </span>
              <p className="text-[10px] text-muted-foreground/70 leading-none mt-0.5">
                Message #{message?.id} of 100
              </p>
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Dismiss"
          >
            <Icon name="X" size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {firstName && (
            <p className="text-sm text-muted-foreground mb-3">
              Good to see you, <span className="font-semibold text-foreground">{firstName}</span>! 👋
            </p>
          )}
          <blockquote className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
            "{message?.message}"
          </blockquote>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 space-y-2">
          <button
            onClick={onDismiss}
            className={`w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-colors ${accent?.btn}`}
          >
            Continue 🚀
          </button>
          <p className="text-center text-[11px] text-muted-foreground/60">
            This message will remain visible as a banner after you continue.
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Dashboard Banner ─────────────────────────────────────────────────────────

export const MotivationalBanner = ({ message, userName, onClose }) => {
  if (!message) return null;
  const accent = getAccent(message?.id);
  const firstName = userName?.split(' ')?.[0] || userName || '';

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r ${accent?.bg} border ${accent?.border} animate-fade-in`}>
      <div className={`flex-shrink-0 p-1.5 rounded-lg bg-background/40`}>
        <Icon name="Sparkles" size={15} className={accent?.icon} />
      </div>
      <p className="flex-1 text-sm text-foreground font-medium leading-snug">
        {firstName && <span className="font-semibold">{firstName}, </span>}
        "{message?.message}"
      </p>
      <button
        onClick={onClose}
        className="flex-shrink-0 p-1 rounded-lg hover:bg-background/40 transition-colors text-muted-foreground hover:text-foreground"
        aria-label="Close banner"
      >
        <Icon name="X" size={14} />
      </button>
    </div>
  );
};

export default MotivationalPopup;
